/**
 * useSVGGeneration Hook
 *
 * Extracts SVG generation logic from ContainerSVG into a reusable React hook.
 * This hook generates all SVG elements (atoms, bonds, electrons, charges, svgArrows)
 * and calculates the optimal viewBox for rendering chemical structures.
 *
 * @package @alchemiesolns/svg-container
 * @feature 003-usesvggeneration-extract-the
 */

import { useMemo, useRef, type JSX } from "react";
import { MolecularWorkspace, makeCompoundId } from "@alchemiesolns/molecular-workspace";
import { type VisibilityConfigDef, VisibilityFlag } from "@alchemiesolns/visibility-config";
import { buildRenderContainer } from "./buildRenderContainer";
import { svgPaperAtoms } from "./svgPaperAtoms";
import { svgPaperArrows } from "./svgPaperArrows";
import { svgElectrons, type ElectronData } from "./svgElectrons";
import { svgElements, resetLonePairIndices } from "./svgElements";
import { svgArrows } from "./svgArrows";
import { svgCharge } from "./svgCharge";
import { calculateOptimalViewBox } from "./svgBounds";
import { getOffsetX, calculateGroupOffset } from "@alchemiesolns/line-angle-util";

// Re-export types for external use
export type { ElectronData };

/**
 * Charge render data with atom ID and direction offset
 */
export interface ChargeData {
    /** Atom identifier (e.g., "atom-1") */
    atomId: string;
    /** Charge label ("+", "_", "2+", "2-", etc.) */
    chg: string;
    /** Atom position in SVG coordinates (y-down) */
    pos: [number, number, number];
    /** Direction offset from atom center (always normalized [1, -1, 0]) */
    dir: [number, number, number];
    /** Whether the atom is a visible small carbon (paper carbon) */
    isPaperCarbon: boolean;
}

/**
 * Arrow render data with source and destination attachment points
 */
export interface ArrowData {
    /** Arrow index for keying */
    index: number;
    /** Arrow type (pa, pb, ba, bb) */
    type: 'pa' | 'pb' | 'ba' | 'bb';

    /** Source atom ID */
    sourceAtomId: string;
    /** Source type: lonepair for pa/pb, bond for ba/bb */
    sourceType: 'lonepair' | 'bond';
    /** For bond source (ba/bb): IDs of atoms forming the bond */
    sourceBondAtomIds?: [string, string];

    /** Destination atom ID (for pa/ba ending at atom) */
    destAtomId?: string;
    /** Destination bond atom IDs (for pb/bb ending at bond) */
    destBondAtomIds?: [string, string];
    /** Destination type: atom or bond */
    destType: 'atom' | 'bond';
    /** Distance offset for arrow endpoint */
    destOffset: number;
}

import { type ArrowConfigDef } from "@alchemiesolns/arrow-def";
import { Rings } from "@alchemiesolns/atomcontainer";
import { resolveAllArrowIds } from "@alchemiesolns/workspace-serialization";
import { type BackgroundRectProps } from "./types/useSVGGeneration.types";
import { useFormatOverride } from "./contexts/FormatOverrideContext";
import { setFormat } from "./utils/formatContext";
import { generateBondRecipes } from "./bondRecipes";
import { BondRecipe } from "./types/bondRecipe.types";
import { generateAtomRecipes } from "./atomRecipes";
import { AtomRecipe } from "./renderAtomFromRecipe";

// Re-export BondRecipe and AtomRecipe for components that need them
export type { BondRecipe, AtomRecipe };

/**
 * Parameters for the useSVGGeneration hook
 *
 * Note: This interface documents the hook's parameters but is not directly used
 * in the function signature (individual parameters are used instead).
 *
 * @example
 * const workspace = new MolecularWorkspace();
 * const { atoms, bonds, viewBox } = useSVGGeneration(workspace, 400, 300);
 */
export interface SVGGenerationParams {
    /**
     * MolecularWorkspace instance containing atoms, bonds, arrows, and visibility settings
     * Coordinates are in y-up (3D chemistry) format - will be converted to y-down (SVG) internally
     * Arrows are extracted from workspace.getArrows()
     */
    workspace: MolecularWorkspace;

    /**
     * Width of the SVG viewport in pixels
     * Must be a positive number
     */
    svgWidth: number;

    /**
     * Height of the SVG viewport in pixels
     * Must be a positive number
     */
    svgHeight: number;

    /**
     * Horizontal alignment of molecule within viewport
     * Default: 'center'
     * - 'left': Align to left edge
     * - 'center': Center horizontally
     * - 'right': Align to right edge
     */
    alignment?: 'left' | 'center' | 'right';
}

/**
 * Atom render data with position separated from graphics
 */
export interface AtomRenderData {
    /** Atom identifier (e.g., "atom-1") */
    id: string;
    /** X position in SVG coordinates (y-down) */
    x: number;
    /** Y position in SVG coordinates (y-down) */
    y: number;
    /** Graphics rendered at origin (0, 0) - position applied by wrapper */
    graphics: JSX.Element;
    /** Visibility flag - if "hidden", atom and attached bonds should not be visible (but still participate in dragging) */
    visibility: "visible" | "hidden";
    /** Display symbol for the atom (e.g., "C", "Cl", "CH3") - used for dynamic hitbox sizing */
    displaySymbol: string;
    /** Atom type: "text" for labeled atoms, "carbon" for small paper carbons */
    atomType: 'text' | 'carbon';
    /** Hydrogen offset for centering non-hydrogen part of symbol (negative = shift left, positive = shift right) */
    hydrogenOffset: number;
}

/**
 * Result returned by the useSVGGeneration hook
 *
 * Contains all JSX elements needed to render a chemical structure,
 * plus the viewBox string for the SVG container.
 *
 * @example
 * const { atoms, bonds, electrons, charges, svgArrows, viewBox } = useSVGGeneration(
 *   config,
 *   400,
 *   300,
 *   0.1
 * );
 */
export interface SVGGenerationResult {
    /**
     * Atom recipes containing precalculated display symbols and metadata
     *
     * Each recipe contains format-independent data that can be rendered
     * by `renderAtomFromRecipe()` with current atom positions.
     *
     * Use `renderAtomFromRecipe()` for static rendering,
     * or for motion rendering with dynamic positions.
     *
     * Empty array if molecule has no atoms.
     */
    atomRecipes: AtomRecipe[];

    /**
     * Structured atom data with position and graphics separated
     *
     * Each atom:
     * - `id`: matching atom ID from AtomContainer
     * - `x, y`: position in SVG coordinates (y-down)
     * - `graphics`: JSX element rendered at origin, needs positioning wrapper
     *
     * Position atoms using transform or Motion:
     * ```tsx
     * // Static:
     * <g transform={`translate(${atom.x}, ${atom.y})`}>{atom.graphics}</g>
     *
     * // Motion:
     * <motion.g style={{x: motionX, y: motionY}}>{atom.graphics}</motion.g>
     * ```
     *
     * Empty array if molecule has no visible atoms.
     */
    atoms: AtomRenderData[];

    /**
     * Bond recipes containing precalculated format-dependent data
     *
     * Each recipe contains:
     * - Bond metadata (id, atom IDs, order, type)
     * - Tube offsets and lengths for double/triple bonds
     * - Element symbols and neighbor counts for endpoint calculations
     * - Special bond data (wedge/dash/squiggle) if applicable
     *
     * Recipes work with both static positions and MotionValues.
     * Use `renderBondFromRecipe()` for static rendering,
     * or `DynamicBond` component for motion rendering.
     *
     * Empty array if molecule has no bonds.
     */
    bondRecipes: BondRecipe[];

    /**
     * Electron data (lone pairs and radicals) with atom IDs and offset directions
     *
     * Each electron data contains:
     * - `atomId`: ID of the atom this electron belongs to
     * - `pos`: base atom position [x, y, z] in SVG coordinates (y-down)
     * - `dir`: offset vector [dx, dy, dz] from atom center to electron position
     * - `type`: "lonepair" or "radical"
     *
     * For motion rendering, subscribe to atom MotionValues and add the offset.
     * Empty array if molecule has no electrons to display.
     */
    electronData: ElectronData[];

    /**
     * Charge data with atom IDs and direction offsets
     *
     * Each charge data contains:
     * - `atomId`: ID of the atom this charge belongs to
     * - `chg`: charge label ("+", "_", "2+", "2-", etc.)
     * - `pos`: atom position [x, y, z] in SVG coordinates (y-down)
     * - `dir`: always normalized [1, -1, 0] (upper-right direction)
     *
     * For motion rendering, subscribe to atom MotionValues and add the offset.
     * Empty array if no charged atoms in molecule.
     */
    chargeData: ChargeData[];

    /**
     * Arrow data with source and destination attachment points
     *
     * Each arrow data contains:
     * - `index`: arrow index for keying
     * - `type`: arrow type (pa, pb, ba, bb)
     * - `sourceAtomId`: source atom ID
     * - `sourceType`: 'lonepair' or 'bond'
     * - `destAtomId`: destination atom ID (for pa/ba)
     * - `destType`: 'atom' or 'bond'
     *
     * For motion rendering, recalculate arrow curve from dynamic atom positions.
     * Empty array if no arrows in molecule.
     */
    arrowData: ArrowData[];

    /**
     * JSX elements representing electrons (lone pairs and radicals)
     *
     * Each lone pair element:
     * - Has `id` in format "{atomId}-lp{1-4}"
     * - Has `data-atom-id` attribute referencing parent atom
     * - Has `data-lp-index` attribute ("1", "2", "3", or "4")
     * - Has `className` including "svg-lonepair"
     *
     * Each radical element:
     * - Has `className` including "svg-radical"
     *
     * Empty array if no electrons to display.
     */
    electrons: JSX.Element[];

    /**
     * JSX elements representing charges (+/-) on atoms
     *
     * Positioned near charged atoms.
     * Empty array if no formal charges in molecule.
     */
    charges: JSX.Element[];

    /**
     * JSX elements representing electron movement svgArrows
     *
     * Used in mechanism problems to show bond formation/breaking.
     * Empty array if config has no arrow definitions.
     */
    arrows: JSX.Element[];

    /**
     * SVG viewBox attribute value defining coordinate system
     *
     * Format: "minX minY width height" (e.g., "0 0 200 150")
     * Calculated dynamically from current atom positions with padding.
     * Always present, even for empty molecules (returns default viewBox).
     *
     * Note: Named `calculatedViewBox` to indicate this is dynamically recalculated.
     * For interactive contexts, use `useLockedViewBox(calculatedViewBox)` to prevent disorienting shifts.
     * For direct use, alias as: `{ calculatedViewBox: viewBox }`
     */
    calculatedViewBox: string;

    /**
     * The decoded visibility definitions from the workspace
     *
     * These are the VisibilityConfigDef objects that were used to render the structure.
     */
    visDefs: import("@alchemiesolns/visibility-config").VisibilityConfigDef[];

    /**
     * Background rectangle properties for zoom/pan integration
     *
     * Spread these props onto a <rect> element in your SVG to enable zoom/pan functionality:
     * ```tsx
     * <svg viewBox={viewBox}>
     *   <rect {...backgroundRect} />
     *   {atoms}
     *   {bonds}
     * </svg>
     * ```
     *
     * The background rect:
     * - Covers the entire SVG viewBox (100% width/height)
     * - Has data-svg-background attribute for event targeting
     * - Has transparent fill to allow click-through to background
     * - Is styled with grab/grabbing cursors via CSS
     *
     * @since 012-svg-zoom-pan
     */
    backgroundRect: BackgroundRectProps;
}

/**
 * React hook for generating SVG elements for chemical structure visualization
 *
 * This hook extracts and memoizes the complex SVG generation logic, making it
 * reusable across different components (ContainerSVG, ArrowDrawingSVG, etc.).
 *
 * The hook converts workspace coordinates (y-up) to SVG coordinates (y-down) internally.
 *
 * @param workspace - MolecularWorkspace instance containing atoms, bonds, arrows, and visibility
 * @param svgWidth - Width of SVG viewport in pixels
 * @param svgHeight - Height of SVG viewport in pixels
 * @returns SVGGenerationResult containing all rendered elements and viewBox
 * @throws Error if workspace processing fails
 *
 * @example
 * import { useSVGGeneration } from '@alchemiesolns/svg-container';
 * import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
 *
 * function MoleculeViewer({ workspace }: { workspace: MolecularWorkspace }) {
 *   const { atoms, bonds, viewBox } = useSVGGeneration(workspace, 400, 300);
 *   return (
 *     <svg width={400} height={300} viewBox={viewBox}>
 *       {atoms}
 *       {bonds}
 *     </svg>
 *   );
 * }
 */
export function useSVGGeneration(
    workspace: MolecularWorkspace,
    svgWidth: number,
    svgHeight: number,
    alignment: 'left' | 'center' | 'right' = 'center'
): SVGGenerationResult {
    // Get format with runtime overrides applied (dev control panel)
    const format = useFormatOverride();

    // Cache ring detection results — rings only change when bond topology changes,
    // not on position updates, visibility toggles, or selection changes (PIPH-63)
    const ringCacheRef = useRef<{
      atomCount: number;
      bondCount: number;
      ringBonds: any[];
    } | null>(null);

    return useMemo(() => {
        try {
            // Set format for this render (with runtime overrides from dev panel)
            setFormat(format);

            // Reset lone pair indices for each render (ensures deterministic IDs)
            resetLonePairIndices();

            // Build a lightweight render-only container directly from the workspace.
            // This skips toAtomContainer's class-instance overhead, the
            // ElementHelper.getAtomicNumberBySymbol lookups, and the separate y-flip
            // mutation pass — buildRenderContainer y-flips inline and pre-builds
            // adjacency for O(1) getBondsAttachedTo. (PIPH-68)
            const container = buildRenderContainer(workspace);

            // Build atom ID → index map once (O(n)), used for all subsequent lookups (O(1) each)
            const atomIdToContainerIndex = new Map<string, number>();
            container.atoms.forEach((atom, i) => { atomIdToContainerIndex.set(atom.id!, i); });

            // Extract visibility definitions from workspace
            // Only include non-Visible atoms (Visible=0 is the default, no visDef needed)
            const visDefs: VisibilityConfigDef[] = [];
            // Also build index → visDef map for O(1) lookups
            const visDefByIndex = new Map<number, VisibilityConfigDef>();
            for (const id of workspace.getAtomIds()) {
                const vis = workspace.getVisibility(id);
                const atomIndex = atomIdToContainerIndex.get(id) ?? -1;
                if (atomIndex !== -1 && vis !== undefined && vis !== VisibilityFlag.Visible) {
                    const vd = { a: atomIndex, visibilityFlag: vis as any };
                    visDefs.push(vd);
                    visDefByIndex.set(atomIndex, vd);
                }
            }

            // Extract arrows from workspace and resolve IDs to indices for SVG rendering
            const arrowDefs = resolveAllArrowIds(workspace);

            // Get all atoms, bonds, and electrons (no filtering)
            const atoms = container.atoms;
            const bonds = container.bonds;

            // Filter lone pairs based on visibility - exclude atoms with HiddenLP or ForceTextHiddenLP flag
            const lps = container.atoms.filter(a => {
                if (a.lonePairs === 0) return false;
                const atomIndex = atomIdToContainerIndex.get(a.id!) ?? -1;
                const visDef = visDefByIndex.get(atomIndex);
                return visDef?.visibilityFlag !== VisibilityFlag.HiddenLP &&
                       visDef?.visibilityFlag !== VisibilityFlag.ForceTextHiddenLP;
            });

            const rads = container.atoms.filter(a => a.radicals > 0);

            // Use cached ring detection if topology unchanged (PIPH-63)
            const currentAtomCount = atoms.length;
            const currentBondCount = bonds.length;
            let ringBonds: any[];
            if (ringCacheRef.current &&
                ringCacheRef.current.atomCount === currentAtomCount &&
                ringCacheRef.current.bondCount === currentBondCount) {
              ringBonds = ringCacheRef.current.ringBonds;
            } else {
              const rings = Rings.getRingBonds(container);
              ringBonds = Array.from(new Set(rings.flat()));
              ringCacheRef.current = { atomCount: currentAtomCount, bondCount: currentBondCount, ringBonds };
            }
            const regularBondRecipes = generateBondRecipes(bonds, ringBonds, container, visDefs);

            // Generate recipes for connective bonds (between instances and loose atoms).
            // These are kept separate from the ring detection pipeline but render
            // through the same BondLayer/DynamicBond path.
            const cBonds = (container as any).connectiveBonds as typeof bonds | undefined;
            const connectiveBondRecipes = cBonds?.length
              ? generateBondRecipes(cBonds, [], container, visDefs)
              : [];
            const bondRecipes = regularBondRecipes.concat(connectiveBondRecipes);

            // Filter visible atoms for layout calculations (excludes Hidden atoms, includes HiddenLP)
            const visibleAtoms = atoms.filter(a => {
                const atomIndex = atomIdToContainerIndex.get(a.id!) ?? -1;
                const visDef = visDefByIndex.get(atomIndex);
                return visDef?.visibilityFlag !== VisibilityFlag.Hidden;
            });

            // Generate atom recipes with pre-calculated display symbols
            const atomRecipes = generateAtomRecipes(atoms, container, visDefs, visibleAtoms);

            // Build recipe lookup map for O(1) access
            const recipeByAtomId = new Map<string, AtomRecipe>();
            for (const r of atomRecipes) { recipeByAtomId.set(r.atomId, r); }

            // Generate atom render data with position separated from graphics
            const atomRenderData: AtomRenderData[] = atoms.map((a) => {
                const atomIndex = atomIdToContainerIndex.get(a.id!) ?? -1;
                const visDef = visDefByIndex.get(atomIndex);
                const visibility = visDef?.visibilityFlag === VisibilityFlag.Hidden ? "hidden" : "visible";

                // Find the corresponding recipe to get displaySymbol and type
                const recipe = recipeByAtomId.get(a.id!);
                const displaySymbol = recipe?.displaySymbol ?? a.symbol ?? '';
                const atomType = recipe?.type ?? 'text';

                // Calculate offset for hitbox positioning
                // Only apply to text atoms, not paper carbons (which are small circles)
                let hydrogenOffset = 0;
                if (atomType === 'text') {
                    if (recipe?.isGroup && recipe.bondDirection) {
                        // Group offset for multi-character labels
                        hydrogenOffset = calculateGroupOffset(displaySymbol, recipe.bondDirection, format.fontSize);
                    } else {
                        // Regular hydrogen offset for non-group atoms
                        hydrogenOffset = getOffsetX(displaySymbol, format.fontSize);
                    }
                }

                return {
                    id: a.id!,
                    x: a.pos!.x,
                    y: a.pos!.y,
                    graphics: svgPaperAtoms.drawAtom(a, container, visibleAtoms, visDefs),
                    visibility,
                    displaySymbol,
                    atomType,
                    hydrogenOffset
                };
            });

            const hydrogenDirections = atoms.map((a) => ({
                id: a.id!,
                dir: svgPaperAtoms.calcHydrogenDirection(a, container, visibleAtoms),
            }));

            // Filter bonds to only include those where both atoms are visible
            // This ensures lone pair positioning correctly adapts when atoms are hidden
            const visibleBonds = bonds.filter(bond => {
                const atom1Index = atomIdToContainerIndex.get(bond.a1.id!) ?? -1;
                const atom2Index = atomIdToContainerIndex.get(bond.a2.id!) ?? -1;
                const visDef1 = visDefByIndex.get(atom1Index);
                const visDef2 = visDefByIndex.get(atom2Index);

                return visDef1?.visibilityFlag !== VisibilityFlag.Hidden &&
                       visDef2?.visibilityFlag !== VisibilityFlag.Hidden;
            });

            const electronCoords = svgElectrons.calcElectronPositions(
                lps,
                rads,
                visibleBonds,
                hydrogenDirections,
                container,
                visibleAtoms
            );
            const electrons = electronCoords.map((electronCoord) =>
                svgElements.electrons(electronCoord, container, visDefs)
            );

            const arrowUtil = svgArrows.calcArrowAtts(
                arrowDefs,
                container,
                electronCoords,
                visibleAtoms
            );
            const arrowElements = svgPaperArrows.drawArrowTube(arrowUtil, arrowDefs);

            // Charge positions - using empty array since workspace doesn't store custom positions
            // Charges will be calculated from atom formal charges automatically
            const chargePositions: any[] = [];
            const chargeCoords = svgCharge.calcChargeCoords(
                container,
                chargePositions,
                format.charge.radius,
                visibleAtoms
            );
            const charges = chargeCoords.map((chargeData, index) => svgElements.charge(chargeData, index));

            // Create charge data with atom IDs for motion tracking
            // Direction is always normalized [1, -1, 0] (upper-right)
            const chargeDirection = [1, -1, 0];
            const chargeDirLength = Math.sqrt(chargeDirection[0] * chargeDirection[0] + chargeDirection[1] * chargeDirection[1]);
            const normalizedChargeDir: [number, number, number] = [
                chargeDirection[0] / chargeDirLength,
                chargeDirection[1] / chargeDirLength,
                0
            ];

            const chargeData: ChargeData[] = container.atoms
                .filter(a => a.charge !== 0)
                .map(a => ({
                    atomId: a.id!,
                    chg: svgCharge.getChargeLabel(a.charge),
                    pos: [a.pos!.x, a.pos!.y, a.pos!.z ?? 0] as [number, number, number],
                    dir: normalizedChargeDir,
                    isPaperCarbon: svgPaperAtoms.isPaperCarbon(a, container, visibleAtoms)
                }));

            // Create arrow data for motion tracking
            // Filter to common arrow types: pa (LP→atom), pb (LP→bond), ba (bond→atom), bb (bond→bond)
            const arrowData: ArrowData[] = arrowDefs
                .filter((def: ArrowConfigDef) => ['pa', 'pb', 'ba', 'bb'].includes(def.type))
                .map((def: ArrowConfigDef, index: number) => {
                    const sourceAtom = atoms[def.src];

                    // Determine source type and IDs
                    const sourceType = (def.type === 'pa' || def.type === 'pb') ? 'lonepair' : 'bond';
                    let sourceBondAtomIds: [string, string] | undefined;
                    let actualSourceAtomId: string;

                    if (sourceType === 'bond') {
                        // For bond source, def.src is bond index
                        const bond = container.bonds[def.src];
                        sourceBondAtomIds = [bond.a1.id!, bond.a2.id!];

                        // Use anchor atom (p) if available, otherwise default to first atom
                        if (def.p !== undefined) {
                            const anchorAtom = atoms[def.p];
                            actualSourceAtomId = anchorAtom.id!;
                        } else {
                            // Fallback to first atom if no anchor specified
                            actualSourceAtomId = sourceBondAtomIds[0];
                        }
                    } else {
                        // For lone pair source, use the source atom directly
                        actualSourceAtomId = sourceAtom.id!;
                    }

                    // Determine destination type and IDs
                    const destType = (def.type === 'pa' || def.type === 'ba') ? 'atom' : 'bond';
                    let destAtomId: string | undefined;
                    let destBondAtomIds: [string, string] | undefined;

                    if (destType === 'atom') {
                        const destAtom = atoms[def.dst];
                        destAtomId = destAtom.id!;
                    } else {
                        // For bond destination, def.dst is bond index
                        const bond = container.bonds[def.dst];
                        destBondAtomIds = [bond.a1.id!, bond.a2.id!];
                    }

                    return {
                        index,
                        type: def.type as 'pa' | 'pb' | 'ba' | 'bb',
                        sourceAtomId: actualSourceAtomId,  // Use the correctly determined source atom
                        sourceType,
                        sourceBondAtomIds,
                        destAtomId,
                        destBondAtomIds,
                        destType,
                        destOffset: 1.2, // Standard offset
                    };
                });

            // Calculate bounds from visible atom positions only (in paper.js coordinates)
            let minX = Infinity;
            let maxX = -Infinity;
            let minY = Infinity;
            let maxY = -Infinity;

            visibleAtoms.forEach(atom => {
                if (atom.pos) {
                    minX = Math.min(minX, atom.pos.x);
                    maxX = Math.max(maxX, atom.pos.x);
                    minY = Math.min(minY, atom.pos.y);
                    maxY = Math.max(maxY, atom.pos.y);
                }
            });

            // Include instance atom positions in bounds (PIPH-77)
            for (const instance of workspace.getGroupInstances()) {
                const template = workspace.getTemplate(instance.templateId);
                if (!template) continue;
                for (const localId of template.workspace.getLooseAtomIds()) {
                    const resolved = workspace.getAtom(makeCompoundId(instance.id, localId));
                    if (resolved) {
                        // Y-FLIP: workspace y-up → SVG y-down
                        const svgY = -resolved.y;
                        minX = Math.min(minX, resolved.x);
                        maxX = Math.max(maxX, resolved.x);
                        minY = Math.min(minY, svgY);
                        maxY = Math.max(maxY, svgY);
                    }
                }
            }

            const bounds = {
                minX,
                maxX,
                minY,
                maxY,
                width: maxX - minX,
                height: maxY - minY
            };

            // console.log("[svgBounds] alignment!", alignment)
            const calculatedViewBox = calculateOptimalViewBox(svgWidth, svgHeight, bounds, alignment);

            // Parse the calculatedViewBox to get actual coordinates for background rect
            const viewBoxParts = calculatedViewBox.split(' ').map(Number);
            const [vbX, vbY, vbWidth, vbHeight] = viewBoxParts;

            // Create background rect that matches the viewBox coordinates
            const backgroundRect = {
                x: vbX,
                y: vbY,
                width: vbWidth,
                height: vbHeight,
                fill: 'transparent',
                'data-svg-background': true as const,
            };

            return {
                atomRecipes,
                atoms: atomRenderData,
                bondRecipes,
                electronData: electronCoords,
                electrons,
                chargeData,
                arrowData,
                arrows: arrowElements,
                charges,
                calculatedViewBox,
                visDefs,
                backgroundRect
            };
        } catch (error) {
            // Invalid workspace or processing error - throw for ErrorBoundary to catch
            console.error('[useSVGGeneration] Error details:', error);
            throw new Error(`Failed to generate SVG elements from workspace: ${error instanceof Error ? error.message : String(error)}`);
        }
    }, [workspace, svgHeight, svgWidth, format, alignment]);
}
